import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Box,
  Typography,
  Chip,
} from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const DeviceTable = ({
  sortedRows,
  selectedDeviceIds,
  selectAll,
  visibleColumns,
  columnOrder,
  sortConfig,
  theme,
  onSelectAll,
  onRowSelect,
  onSort,
  onDeviceClick,
  onOwnerClick,
  getStatusStyle,
  calculateDeviceUptime,
  getUptimeColor,
  dashboardDevices,
  dataDashboard,
  isMobile,
}) => {
  const { t } = useTranslation('dashboard');
  // Колонки которые скрываем на мобилке
  const mobileHiddenColumns = ['mac', 'serialNumber', 'algorithm', 'worker', 'client', 'tags', 'fan'];
  // Дефолтный порядок колонок (на случай если columnOrder не передан)
  const defaultColumnOrder = ['modelName', 'ip', 'status', 'hashrate', 'temperature', 'uptime', 'worker', 'client', 'fan', 'model', 'algorithm', 'mac', 'serialNumber', 'tags'];
  const orderedColumns = columnOrder || defaultColumnOrder;

  // Функция для получения started_at из открытого диапазона offline
  const getOfflineStartedAt = (deviceId) => {
    const deviceData = dataDashboard?.find(data => data.device_id === deviceId);
    if (!deviceData || !deviceData.uptime || !Array.isArray(deviceData.uptime)) {
      return null;
    }

    // Ищем запись со статусом offline и ended_at === null
    const offlineRecord = deviceData.uptime.find(
      record => record.status === 'offline' && record.ended_at === null
    );

    return offlineRecord?.started_at || null;
  };

  // Функция для расчета времени нахождения в offline
  const getOfflineDuration = (startedAt) => {
    if (!startedAt) return null;

    const now = new Date();
    const startDate = new Date(startedAt);
    const diffMs = now - startDate;

    // Если разница отрицательная или некорректная, возвращаем null
    if (diffMs < 0 || isNaN(diffMs)) return null;

    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return `${diffDays}д ${diffHours % 24}ч`;
    } else if (diffHours > 0) {
      return `${diffHours}ч ${diffMinutes % 60}м`;
    } else if (diffMinutes > 0) {
      return `${diffMinutes}м`;
    } else {
      return `${diffSeconds}с`;
    }
  };

  // Функция для получения правил алертов устройства
  const getDeviceAlertRules = (deviceId) => {
    const device = dashboardDevices?.find(d => d.id === deviceId);
    return device?.alert_rule_devices && device.alert_rule_devices.length > 0
      ? device.alert_rule_devices[0]
      : null;
  };

  // Функция для расчета цвета температуры/вентилятора
  const getMetricColor = (value, min, max) => {
    if (min == null || max == null) {
      return theme.palette.text.secondary; // Серый, если нет правил
    }

    // Если выше max - красный
    if (value > max) {
      return theme.palette.error.main;
    }

    // Плавный переход от зеленого к оранжевому
    const ratio = (value - min) / (max - min);
    const clampedRatio = Math.max(0, Math.min(1, ratio));

    // От зеленого (120°) к оранжевому (30°)
    const hue = 120 - clampedRatio * 90;
    return `hsl(${hue}, 70%, 50%)`;
  };

  // Функция для расчета цвета хешрейта
  const getHashrateColor = (value, min, max) => {
    if (min == null || max == null) {
      return theme.palette.success.main; // Зеленый по умолчанию, если нет правил
    }

    // Если выше max или ниже min - красный
    if (value > max || value < min) {
      return theme.palette.error.main;
    }

    // Вычисляем расстояние от границ
    const range = max - min;

    if (range === 0) {
      return theme.palette.success.main;
    }

    // Расстояние от ближайшей границы
    const distanceFromMin = Math.abs(value - min);
    const distanceFromMax = Math.abs(value - max);
    const minDistance = Math.min(distanceFromMin, distanceFromMax);

    // Нормализуем расстояние (0 = на границе, 1 = в центре)
    const normalizedDistance = (minDistance / (range / 2));
    const clampedDistance = Math.max(0, Math.min(1, normalizedDistance));

    // Цвет от желтого (у границ) к зеленому (в центре)
    // Зеленый: hue = 120, Желтый: hue = 60
    const hue = 60 + clampedDistance * 60;
    return `hsl(${hue}, 70%, 50%)`;
  };

  // Конфигурация колонок - заголовки
  const columnHeaders = {
    model: {
      label: t('columnShortModel'),
      sortable: true,
      sortKey: 'model',
    },
    modelName: {
      label: t('columnDevice'),
      sortable: true,
      sortKey: 'modelName',
    },
    status: {
      label: t('columnStatus'),
      sortable: true,
      sortKey: 'status',
    },
    algorithm: {
      label: t('columnAlgorithm'),
      sortable: true,
      sortKey: 'algorithm',
    },
    mac: {
      label: 'MAC',
      sortable: false,
    },
    ip: {
      label: t('columnIp'),
      sortable: true,
      sortKey: 'ip',
    },
    serialNumber: {
      label: t('columnSerial'),
      sortable: false,
    },
    tags: {
      label: t('columnTags'),
      sortable: false,
    },
    hashrate: {
      label: t('columnHashrate'),
      sortable: true,
      sortKey: 'hashrate',
    },
    temperature: {
      label: t('columnTemp'),
      sortable: false,
    },
    fan: {
      label: t('columnFan'),
      sortable: false,
    },
    worker: {
      label: t('columnWorker'),
      sortable: true,
      sortKey: 'worker',
    },
    uptime: {
      label: t('columnUptime'),
      sortable: true,
      sortKey: 'uptime',
    },
    client: {
      label: t('columnClient'),
      sortable: true,
      sortKey: 'client',
    },
  };

  // Рендер заголовка колонки
  const renderColumnHeader = (columnKey) => {
    const config = columnHeaders[columnKey];
    if (!config) return null;

    const isVisible = visibleColumns[columnKey];
    if (!isVisible) return null;

    if (isMobile && mobileHiddenColumns.includes(columnKey)) return null;

    const isSortable = config.sortable;
    const sortKey = config.sortKey;

    return (
      <TableCell
        key={columnKey}
        style={{
          cursor: isSortable ? 'pointer' : 'default',
          userSelect: isSortable ? 'none' : 'auto',
        }}
        onClick={isSortable ? () => onSort(sortKey) : undefined}
      >
        {config.label}
        {isSortable && sortConfig.key === sortKey && (
          sortConfig.direction === 'asc' ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />
        )}
      </TableCell>
    );
  };

  // Рендер ячейки данных
  const renderColumnCell = (columnKey, row) => {
    const isVisible = visibleColumns[columnKey];
    if (!isVisible) return null;

    if (isMobile && mobileHiddenColumns.includes(columnKey)) return null;

    switch (columnKey) {
      case 'model':
        return (
          <TableCell key={columnKey}>
            <span
              style={{ color: theme.palette.primary.main, cursor: 'pointer' }}
            >
              {row.model}
            </span>
          </TableCell>
        );

      case 'modelName':
        return (
          <TableCell key={columnKey}>
            <span style={{ color: theme.palette.primary.main, cursor: 'pointer' }}>
              {row.modelName || row.model || 'N/A'}
            </span>
          </TableCell>
        );

      case 'status':
        return (
          <TableCell key={columnKey}>
            {(() => {
              let statusText;

              if (row.status === 'online') {
                statusText = 'Online';
              } else if (row.status === 'offline') {
                const startedAt = getOfflineStartedAt(row.id);
                const duration = getOfflineDuration(startedAt);
                statusText = duration ? `Offline (${duration})` : 'Offline';
              } else if (row.status === null) {
                statusText = 'N/A';
              } else {
                statusText = row.status.charAt(0).toUpperCase() + row.status.slice(1);
              }

              return (
                <span style={{ ...getStatusStyle(row.status), fontSize: 12, padding: '2px 8px', lineHeight: '16px', display: 'inline-block' }}>
                  {statusText}
                </span>
              );
            })()}
          </TableCell>
        );

      case 'algorithm':
        return (
          <TableCell key={columnKey}>{row.algorithm}</TableCell>
        );

      case 'mac':
        return (
          <TableCell key={columnKey}>{row.mac}</TableCell>
        );

      case 'ip':
        return (
          <TableCell key={columnKey}>{row.ip || 'N/A'}</TableCell>
        );

      case 'serialNumber':
        return (
          <TableCell key={columnKey}>{row.serialNumber || 'N/A'}</TableCell>
        );

      case 'tags':
        return (
          <TableCell key={columnKey}>
            {Array.isArray(row.tags) && row.tags.length > 0 ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {row.tags.map((tag, i) => (
                  <Chip key={i} label={tag} size="small" variant="outlined" />
                ))}
              </Box>
            ) : ''}
          </TableCell>
        );

      case 'hashrate':
        return (
          <TableCell key={columnKey}>
            {(() => {
              const device = dashboardDevices?.find(d => d.id === row.id);
              const alertRules = getDeviceAlertRules(row.id);
              const latestMetrics = device?.latest_metrics;

              if (row.hashrate === '-' || row.hashrate === 'N/A' || !latestMetrics?.hashrate) {
                return (
                  <span style={{
                    color: theme.palette.text.secondary,
                    fontWeight: 'normal'
                  }}>
                    {row.hashrate}
                  </span>
                );
              }

              const hashrateValue = latestMetrics.hashrate;
              const hashrateMin = alertRules?.hashrate_min;
              const hashrateMax = alertRules?.hashrate_max;
              const color = getHashrateColor(hashrateValue, hashrateMin, hashrateMax);

              return (
                <span style={{
                  color: color,
                  fontWeight: 600
                }}>
                  {row.hashrate}
                </span>
              );
            })()}
          </TableCell>
        );

      case 'temperature':
        return (
          <TableCell key={columnKey}>
            {(() => {
              const alertRules = getDeviceAlertRules(row.id);
              const device = dashboardDevices?.find(d => d.id === row.id);
              const latestMetrics = device?.latest_metrics;

              if (!row.temperature || row.temperature.length === 0) {
                return 'N/A';
              }

              // В таблице — самая горячая плата; все значения в подсказке
              const items = row.temperature.map((temp, index) => {
                const tempKey = `temperature${index}`;
                return { value: Number(temp), label: temp, color: getMetricColor(latestMetrics?.[tempKey], alertRules?.[`${tempKey}_min`], alertRules?.[`${tempKey}_max`]) };
              });
              // N/A и пустые значения в «худшее» не берём
              const known = items.filter((x) => Number.isFinite(x.value));
              if (known.length === 0) return 'N/A';
              const worst = known.reduce((m, x) => (x.value > m.value ? x : m), known[0]);
              return (
                <span title={items.map((x) => `${x.label}°`).join(' · ')} style={{ color: worst.color, fontWeight: 600 }}>
                  {worst.label}°
                </span>
              );
            })()}
          </TableCell>
        );

      case 'fan':
        return (
          <TableCell key={columnKey}>
            {(() => {
              const alertRules = getDeviceAlertRules(row.id);
              const device = dashboardDevices?.find(d => d.id === row.id);
              const latestMetrics = device?.latest_metrics;

              if (!row.fan || row.fan.length === 0) {
                return 'N/A';
              }

              // В таблице — самый медленный вентилятор; все значения в подсказке
              const items = row.fan.map((fanSpeed, index) => {
                const fanKey = `fan_speed${index}`;
                return { value: Number(fanSpeed), label: fanSpeed, color: getMetricColor(latestMetrics?.[fanKey], alertRules?.[`${fanKey}_min`], alertRules?.[`${fanKey}_max`]) };
              });
              // пустой слот с 0 оборотов — не вентилятор; если все по нулям, показываем 0
              const spinning = items.filter((x) => Number.isFinite(x.value) && x.value > 0);
              const known = spinning.length ? spinning : items.filter((x) => Number.isFinite(x.value));
              if (known.length === 0) return 'N/A';
              const worst = known.reduce((m, x) => (x.value < m.value ? x : m), known[0]);
              return (
                <span title={items.map((x) => x.label).join(' · ')} style={{ color: worst.color, fontWeight: 600 }}>
                  {worst.label}
                </span>
              );
            })()}
          </TableCell>
        );

      case 'worker':
        return (
          <TableCell key={columnKey}>
            {row.worker}
          </TableCell>
        );

      case 'uptime':
        return (
          <TableCell key={columnKey}>
            {(() => {
              const uptimePercentage = calculateDeviceUptime(row.id);
              const uptimeColor = getUptimeColor(uptimePercentage);
              return (
                <span
                  style={{
                    color: uptimeColor,
                    fontWeight: 600
                  }}
                >
                  {uptimePercentage}%
                </span>
              );
            })()}
          </TableCell>
        );

      case 'client':
        return (
          <TableCell key={columnKey}>
            {row.owner ? (
              <span
                style={{
                  color: theme.palette.primary.main,
                  cursor: 'pointer'
                }}
                onClick={(e) => { e.stopPropagation(); onOwnerClick(row.owner.id); }}
                title={t('goToProfile', { name: row.owner.name })}
              >
                {row.client}
              </span>
            ) : (
              row.client
            )}
          </TableCell>
        );

      default:
        return null;
    }
  };

  // на мобилке убираем фиксированную ширину и уменьшаем padding ячеек
  return (
    <Box sx={{ mt: '12px' }}>
        <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: 'divider', borderRadius: '12px', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <Table size="small" sx={{
            minWidth: isMobile ? 0 : 860, width: '100%',
            '& th, & td': { whiteSpace: 'nowrap', fontSize: 13, ...(isMobile ? { px: '6px' } : { px: '10px' }) },
            '& th': { fontWeight: 500, fontSize: 12, color: 'text.secondary', py: '8px' },
            '& td': { py: '6px' },
            '& tbody tr': { cursor: 'pointer' },
            '& tbody tr:last-child td': { borderBottom: 0 },
          }}>
            <TableHead sx={{ position: 'sticky', top: 0, backgroundColor: 'background.paper', zIndex: 1 }}>
              <TableRow>
                {/* Ширина ячейки постоянная: счётчик выбранных не должен сдвигать колонки таблицы */}
                <TableCell sx={{ width: isMobile ? 'auto' : 72, minWidth: isMobile ? 0 : 72 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Checkbox
                      size="small"
                      sx={{ p: '4px' }}
                      checked={selectAll || (sortedRows.length > 0 && sortedRows.every(row => selectedDeviceIds.includes(row.id)))}
                      indeterminate={selectedDeviceIds.length > 0 && selectedDeviceIds.length < sortedRows.length}
                      onChange={onSelectAll}
                    />
                    {selectedDeviceIds.length > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        ({selectedDeviceIds.length})
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                {orderedColumns.map(columnKey => renderColumnHeader(columnKey))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRows.map((row, index) => (
                <TableRow key={index} hover onClick={() => { if (!window.getSelection()?.toString()) onDeviceClick(row.id); }}>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      size="small"
                      sx={{ p: '4px' }}
                      checked={selectedDeviceIds.includes(row.id)}
                      onChange={() => onRowSelect(row.id)}
                    />
                  </TableCell>
                  {orderedColumns.map(columnKey => renderColumnCell(columnKey, row))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
    </Box>
  );
};

DeviceTable.propTypes = {
  sortedRows: PropTypes.array.isRequired,
  selectedDeviceIds: PropTypes.array.isRequired,
  selectAll: PropTypes.bool.isRequired,
  visibleColumns: PropTypes.object.isRequired,
  columnOrder: PropTypes.array,
  sortConfig: PropTypes.object.isRequired,
  theme: PropTypes.object.isRequired,
  onSelectAll: PropTypes.func.isRequired,
  onRowSelect: PropTypes.func.isRequired,
  onSort: PropTypes.func.isRequired,
  onDeviceClick: PropTypes.func.isRequired,
  onOwnerClick: PropTypes.func.isRequired,
  getStatusStyle: PropTypes.func.isRequired,
  calculateDeviceUptime: PropTypes.func.isRequired,
  getUptimeColor: PropTypes.func.isRequired,
  dashboardDevices: PropTypes.array,
  dataDashboard: PropTypes.array,
  isMobile: PropTypes.bool,
};

export default DeviceTable;
